import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import '../button/lib-button';
import '../icon/lib-icon';
import css from './lib-calendar.css';

const icons: Record<string, string> = {
  'double-chevron-left': new URL('../wago-icons-svg/double-chevron-left.svg', import.meta.url).href,
  'chevron-left': new URL('../wago-icons-svg/chevron-left.svg', import.meta.url).href,
  'chevron-right': new URL('../wago-icons-svg/chevron-right.svg', import.meta.url).href,
  'double-chevron-right': new URL('../wago-icons-svg/double-chevron-right.svg', import.meta.url).href,
  time_clock: new URL('../wago-icons-svg/time_clock.svg', import.meta.url).href,
};
const parse = (value: string): Date => { const date = new Date(`${value}T12:00:00`); return date; };
const iso = (date: Date): string => `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const today = (): string => iso(new Date());
const addDays = (value: string, days: number): string => { const date = parse(value); date.setDate(date.getDate() + days); return iso(date); };
const addMonths = (value: string, months: number): string => {
  const date = parse(value), day = date.getDate();
  date.setDate(1); date.setMonth(date.getMonth() + months);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(day, end)); return iso(date);
};

/** A localized calendar with single-date and date-range selection. */
@customElement('lib-calendar')
export class LibCalendar extends LibElement {
  protected override get hostFocusSelector(): string { return 'button, lib-button'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property({ attribute: 'today-date' }) todayDate = '';
  private today(): string { return this.todayDate || today(); }
  @property({ attribute: 'selected-date' }) selectedDate = '';
  @property({ attribute: 'range-start' }) rangeStart = '';
  @property({ attribute: 'range-end' }) rangeEnd = '';
  @property({ attribute: 'selection-mode' }) selectionMode = 'SingleDate';
  @property({ type: Boolean, attribute: 'allow-same-day-range' }) allowSameDayRange = false;
  @property({ attribute: 'min-date' }) minDate = '';
  @property({ attribute: 'max-date' }) maxDate = '';
  @property({ attribute: 'initial-view' }) initialView = '';
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean, attribute: 'hide-today' }) hideToday = false;
  @property({ type: Boolean }) error = false;
  @property({ type: Boolean }) card = false;
  @property() culture = 'en-US';
  @property({ type: Number, attribute: 'first-day' }) firstDay = 0;
  @property({ attribute: 'grid-label' }) gridLabel = 'Calendar';
  @property({ attribute: 'today-label' }) todayLabel = 'Today';
  @property({ attribute: 'previous-month-label' }) previousMonthLabel = 'Previous month';
  @property({ attribute: 'next-month-label' }) nextMonthLabel = 'Next month';
  @property({ attribute: 'previous-year-label' }) previousYearLabel = 'Previous year';
  @property({ attribute: 'next-year-label' }) nextYearLabel = 'Next year';
  @property({ attribute: 'label-view' }) labelView = '';
  @property({ attribute: 'month-title' }) monthTitle = '';
  @property({ attribute: 'disabled-from' }) disabledFrom = '';
  @property({ attribute: 'disabled-to' }) disabledTo = '';
  @property({ type: Boolean, attribute: 'await-data' }) awaitData = false;
  @property({ attribute: false }) months: string[] = [];
  @property({ attribute: false }) weekdays: string[] = [];
  @property({ attribute: false }) dayLabels: Record<string, string> = {};
  @property({ attribute: false }) disabledDates: string[] = [];
  @property({ attribute: false }) dataReady = false;
  @state() private view = '';
  @state() private focused = '';
  @state() private hover = '';
  private pendingFocus = false;
  private eventValue: string | null = null;
  get value(): string { return this.eventValue ?? (this.isRange ? `${this.rangeStart}/${this.rangeEnd}` : this.selectedDate); }
  private get isRange(): boolean { return this.selectionMode === 'DateRange'; }
  private unavailable(value: string): boolean {
    return !!(this.disabledFrom && value < this.disabledFrom || this.disabledTo && value > this.disabledTo) || value < '0001-01-01' || value > '9999-12-31' || !!(this.minDate && value < this.minDate || this.maxDate && value > this.maxDate || this.disabledDates.includes(value));
  }
  protected override willUpdate(changed: PropertyValues<this>): void {
    if (this.awaitData && !this.dataReady) return;
    const anchor = this.isRange ? this.rangeStart : this.selectedDate;
    if (!this.view) {
      this.view = (this.initialView || anchor || this.today()).slice(0, 7) + '-01';
      this.focused = this.findSelectable(anchor || this.today(), 1) || this.today();
    } else if ((changed.has('selectedDate') || changed.has('rangeStart') || changed.has('selectionMode')) && anchor && anchor.slice(0, 7) !== this.view.slice(0, 7)) {
      this.setView(anchor);
    }
    if (!this.focused.startsWith(this.view.slice(0, 7)) || this.unavailable(this.focused)) {
      this.focused = this.view;
      for (let day = this.view; day.startsWith(this.view.slice(0, 7)); day = addDays(day, 1)) {
        if (!this.unavailable(day)) { this.focused = day; break; }
      }
    }
  }
  protected override updated(): void {
    if (this.pendingFocus) {
      this.pendingFocus = false;
      this.renderRoot.querySelector<HTMLButtonElement>(`button[data-date="${this.focused}"]`)?.focus();
    }
  }
  private setView(value: string): void {
    if (value < '0001-01-01' || value > '9999-12-31') return;
    this.view = value.slice(0, 7) + '-01';
    this.eventValue = this.view;
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    this.eventValue = null;
  }
  private navigate(months: number): void { if (!this.disabled) { this.hover = ''; this.setView(addMonths(this.view, months)); } }
  private goToday(): void {
    if (this.disabled) return;
    this.setView(this.today()); this.focused = this.findSelectable(this.today(), -1) || this.today();
  }
  private select(value: string): void {
    if (this.disabled || this.unavailable(value)) return;
    this.focused = value; this.hover = '';
    if (!this.isRange) {
      if (this.selectedDate === value) return;
      this.selectedDate = value;
    } else if (this.rangeStart && !this.rangeEnd && value >= this.rangeStart) {
      if (value === this.rangeStart && !this.allowSameDayRange) return;
      this.rangeEnd = value;
    } else { this.rangeStart = value; this.rangeEnd = ''; }
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }
  private findSelectable(value: string, direction: number): string | null {
    for (let i = 0; i < 500 && value >= '0001-01-01' && value <= '9999-12-31'; i++, value = addDays(value, direction)) {
      if (!this.unavailable(value)) return value;
    }
    return null;
  }
  private keydown(event: KeyboardEvent, value: string): void {
    if (this.disabled) return;
    const offset = (parse(value).getDay() - this.firstDay + 7) % 7;
    const targets: Record<string, [string, number]> = {
      ArrowLeft: [addDays(value, -1), -1], ArrowRight: [addDays(value, 1), 1],
      ArrowUp: [addDays(value, -7), -1], ArrowDown: [addDays(value, 7), 1],
      Home: [addDays(value, -offset), 1], End: [addDays(value, 6 - offset), -1],
      PageUp: [addMonths(value, event.shiftKey ? -12 : -1), 1], PageDown: [addMonths(value, event.shiftKey ? 12 : 1), 1],
    };
    const target = targets[event.key]; if (!target) return;
    event.preventDefault();
    const next = this.findSelectable(...target); if (!next) return;
    this.focused = next; this.hover = ''; this.setView(next); this.pendingFocus = true;
  }
  private dayClass(value: string): string {
    const classes = ['lib-calendar-day'];
    const add = (suffix: string) => classes.push(`lib-calendar-day--${suffix}`);
    if (this.disabled || this.unavailable(value)) { add('disabled'); return classes.join(' '); }
    if (value === this.today()) add('today');
    this.selectionClasses(value).forEach(add);
    return classes.join(' ');
  }
  private selectionClasses(value: string): string[] {
    if (!this.isRange) return value === this.selectedDate ? ['selected'] : [];
    if (this.rangeStart && this.rangeEnd) {
      const classes: string[] = [];
      if (value === this.rangeStart) classes.push('range-first');
      if (value === this.rangeEnd) classes.push('range-last');
      if (value > this.rangeStart && value < this.rangeEnd) classes.push('in-range');
      return classes;
    }
    return this.previewClasses(value);
  }
  private previewClasses(value: string): string[] {
    if (!this.rangeStart) return [];
    const classes: string[] = [];
    const preview = this.hover > this.rangeStart;
    if (value === this.rangeStart) classes.push(preview ? 'range-first' : 'selected');
    if (preview && value > this.rangeStart && value <= this.hover) classes.push(value === this.hover ? 'preview-end' : 'in-range');
    return classes;
  }
  private navigation(icon: string, label: string, months: number) {
    return html`<lib-button variant="tertiary" regular ?disabled=${this.disabled} aria-label=${label} title=${label}
      @click=${() => this.navigate(months)}><lib-icon icon-url=${icons[icon]}></lib-icon></lib-button>`;
  }
  private isSelected(value: string): boolean {
    if (this.isRange) return value === this.rangeStart || value === this.rangeEnd;
    return value === this.selectedDate;
  }
  override render() {
    if (!this.view) return nothing;
    const date = parse(this.view);
    const offset = (date.getDay() - this.firstDay + 7) % 7;
    const count = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const weeks = Array.from({ length: Math.ceil((offset + count) / 7) }, (_, week) => Array.from({ length: 7 }, (_, column) => {
      const day = week * 7 + column - offset + 1;
      return day < 1 || day > count ? '' : `${this.view.slice(0, 8)}${String(day).padStart(2, '0')}`;
    }));
    return html`<div class="lib-calendar ${this.disabled ? 'lib-calendar--disabled' : ''} ${this.isRange ? 'lib-calendar--range' : ''} ${this.error ? 'lib-calendar--error' : ''} ${this.card ? 'lib-calendar--card' : ''}">
      <div class="lib-calendar-header">
        <div class="lib-calendar-nav-group">${this.navigation('double-chevron-left', this.previousYearLabel, -12)}${this.navigation('chevron-left', this.previousMonthLabel, -1)}</div>
        <div class="lib-calendar-title" aria-live="polite">${this.labelView === this.view ? this.monthTitle : `${this.months[date.getMonth()] ?? date.toLocaleDateString(this.culture || 'en-GB', { month: 'long' })} ${date.getFullYear()}`}</div>
        <div class="lib-calendar-nav-group">${this.navigation('chevron-right', this.nextMonthLabel, 1)}${this.navigation('double-chevron-right', this.nextYearLabel, 12)}</div>
      </div>
      <div class="lib-calendar-separator" aria-hidden="true"></div>
      <div class="lib-calendar-grid" role="grid" aria-label=${this.gridLabel} aria-invalid=${this.error ? 'true' : nothing} @mouseleave=${() => this.hover = ''}>
        <div class="lib-calendar-week" role="row">${Array.from({ length: 7 }, (_, i) => html`<span class="lib-calendar-weekday" role="columnheader">${this.weekdays[(this.firstDay + i) % 7] ?? new Date(2025, 5, 1 + (this.firstDay + i) % 7).toLocaleDateString(this.culture || 'en-GB', { weekday: 'short' })}</span>`)}</div>
        ${weeks.map(week => html`<div class="lib-calendar-week" role="row">${week.map(value => {
          if (!value) return html`<div class="lib-calendar-cell" role="gridcell"></div>`;
          return html`
          <div class="lib-calendar-cell" role="none">
            <button type="button" role="gridcell" aria-selected=${String(this.isSelected(value))} aria-current=${value === this.today() ? 'date' : nothing} data-date=${value} class=${this.dayClass(value)} ?disabled=${this.disabled || this.unavailable(value)}
              tabindex=${!this.disabled && !this.unavailable(value) && value === this.focused ? 0 : -1}
              aria-label=${this.dayLabels?.[value] ?? parse(value).toLocaleDateString(this.culture || 'en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              @click=${() => this.select(value)} @mouseenter=${() => { if (this.isRange && !this.disabled && !this.unavailable(value) && !this.rangeEnd) this.hover = value; }}
              @keydown=${(event: KeyboardEvent) => this.keydown(event, value)}><span class="lib-calendar-day-number">${parse(value).getDate()}</span></button>
          </div>`;
        })}</div>`)}
      </div>
      ${this.hideToday ? nothing : html`<div class="lib-calendar-footer"><lib-button variant="secondary" ?disabled=${this.disabled} @click=${this.goToday}><lib-icon icon-url=${icons.time_clock}></lib-icon><span>${this.todayLabel}</span></lib-button></div>`}
    </div>`;
  }
}
declare global { interface HTMLElementTagNameMap { 'lib-calendar': LibCalendar; } }

export function setCalendarData(element: LibCalendar | null, months: string[], weekdays: string[],
  dayLabels: Record<string, string>, disabledDates: string[]): void {
  if (!element) return;
  element.months = months;
  element.weekdays = weekdays;
  element.dayLabels = dayLabels;
  element.disabledDates = disabledDates;
  element.dataReady = true;
}
